import { type LucideIcon, Pause, Play } from "lucide-react";
import React from "react";

type StatusBadgeVariant = "active" | "inactive" | "info" | "warning";

const VARIANT_CLASSES: Record<StatusBadgeVariant, { base: string; interactive: string }> = {
  active: {
    base: "border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    interactive: "hover:bg-emerald-500/15",
  },
  inactive: {
    base: "border border-[#F59E0B]/30 bg-[#F59E0B]/10 text-amber-600 dark:text-[#F59E0B]",
    interactive: "hover:bg-[#F59E0B]/15",
  },
  info: {
    base: "border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400",
    interactive: "hover:bg-blue-500/15",
  },
  warning: {
    base: "border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    interactive: "hover:bg-amber-500/15",
  },
};

interface StatusBadgeProps {
  label: string;
  variant: StatusBadgeVariant;
  readOnly?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  icon?: LucideIcon;
  iconClassName?: string;
  showToggleIcon?: boolean;
}

export function StatusBadge({
  label,
  variant,
  readOnly = false,
  disabled = false,
  onClick,
  title,
  icon: Icon,
  iconClassName,
  showToggleIcon = false,
}: StatusBadgeProps) {
  const colors = VARIANT_CLASSES[variant];
  const displayLabel = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

  if (readOnly || !onClick) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-md ${colors.base}`}>
        {displayLabel}
      </span>
    );
  }

  const ToggleIcon = showToggleIcon
    ? variant === "active" ? Pause : Play
    : null;

  const DisplayIcon = Icon ?? ToggleIcon;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md transition-colors duration-150 ${colors.base} ${colors.interactive} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      title={title}
    >
      <div className="flex items-center gap-1.5">
        {displayLabel}
        {DisplayIcon && (
          <DisplayIcon className={`size-1.5 fill-current ${iconClassName ?? ""}`} />
        )}
      </div>
    </button>
  );
}
